import { ReactNode, createContext, useEffect } from "react";
import { LoadingShim } from "@narmi/design_system";
import { useFluentLocalization } from "cerulean";
import { LocalizationProvider } from "@fluent/react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Endpoint } from "@fingerprintjs/fingerprintjs-pro";

// Glia will not accept anything short of these extended locale codes
const GLIA_LOCALE_MAPPING: Record<string, string> = {
  es: "es-MX",
  fr: "fr-CA",
  de: "de-DE",
};

interface LanguageSelectContextInterface {
  showLangSelect: boolean;
  localeCodes: string[];
  localeNames: string[];
  selectedLocale: string | undefined;
  setSelectedLocale: (lang: string) => any;
}
export const LangSelectContext = createContext<LanguageSelectContextInterface>({
  showLangSelect: false,
  localeCodes: ["en"],
  localeNames: ["English"],
  selectedLocale: "en",
  setSelectedLocale: (lang: string) => {}, // eslint-disable-line @typescript-eslint/no-unused-vars
});

export interface AppLocalizationProviderProps {
  /** Application content */
  children: ReactNode;
  /** Institution internal_name */
  internalName: string;
  s3ImagesBucket: string;
  fingerprintApiKey: string;
  fingerprintEndpoint: Endpoint;
  fingerprintScriptSrc: string;
  deviceMonitoringEnabled: boolean;
  isHiddenWhileLoading?: boolean;
}

const AppLocalizationProvider = ({
  internalName,
  s3ImagesBucket,
  isHiddenWhileLoading,
  children,
}: AppLocalizationProviderProps) => {
  const {
    l10n,
    isLoading,
    isMultilingual,
    localeCodes,
    localeNames,
    selectedLocale,
    setSelectedLocale,
  } = useFluentLocalization({
    internalName,
    s3ImagesBucket,
    productCode: "olb",
    apiUrl: "/v1/",
    isMfe: true, // azul is a "microfrontend" due to its multiple React roots
  });

  // Update Glia locale when selectedLocale changes
  useEffect(() => {
    const gliaLocale = GLIA_LOCALE_MAPPING[selectedLocale ?? "en"];
    if (
      gliaLocale &&
      selectedLocale !== "en" &&
      typeof window?.sm !== "undefined"
    ) {
      window.sm
        .getApi({ version: "v1" })
        .then((salemove: Record<string, any>) => {
          try {
            salemove.setLocale(gliaLocale);
          } catch (error) {
            console.warn("Failed to set Glia locale:", error);
          }
        });
    }
  }, [selectedLocale]);

  // Prevents rendering the component until localization is loaded
  if (isLoading && isHiddenWhileLoading) {
    return null;
  }

  return isLoading ? (
    <LoadingShim isLoading>
      <div style={{ height: "100vh" }} />
    </LoadingShim>
  ) : (
    <>
      <LocalizationProvider l10n={l10n}>
        <LangSelectContext.Provider
          value={{
            showLangSelect: isMultilingual || localeCodes.includes("debug"),
            localeCodes,
            localeNames,
            selectedLocale,
            setSelectedLocale,
          }}
        >
          {children}
        </LangSelectContext.Provider>
      </LocalizationProvider>
    </>
  );
};

export default AppLocalizationProvider;
